/**
 * Admin card for rolling out gated features to all users.
 *
 * Every new feature ships gated to the owner account first. When the owner is
 * satisfied, they flip it public here — takes effect immediately, no redeploy.
 * Visible only to admins (endpoint returns 403 otherwise → renders nothing).
 */
import React, { useEffect, useState } from 'react';
import { Rocket, Loader2, Lock, Globe, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';

export default function FeatureGatesAdminCard({ user }) {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // key currently being toggled

  const load = async () => {
    if (!user?.is_admin) { setEntries(false); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await apiService.getFeatureGatesStatus(user.id);
      setEntries(r.data?.entries || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        setEntries(false); // not an admin — render nothing
      } else {
        setEntries([]);
        toast.error('Could not load feature rollout status.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) load();
  }, [user?.id]);

  const toggle = async (key, makePublic) => {
    setBusy(key);
    try {
      await apiService.rolloutFeatureGate(user.id, key, makePublic);
      toast.success(makePublic ? 'Rolled out to all users.' : 'Reverted to your account only.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rollout failed.');
    } finally {
      setBusy(null);
    }
  };

  if (entries === false) return null; // not admin
  if (loading && entries === null) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-2 text-sm text-slate-500" data-testid="feature-gates-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading feature rollout status…
      </div>
    );
  }
  if (!entries || entries.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="feature-gates-card">
      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-blue-50 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <Rocket className="w-4 h-4 text-emerald-600" />
          <div>
            <h3 className="text-sm font-bold text-slate-900">Feature rollout control</h3>
            <p className="text-xs text-slate-500">New features are live on your account only. Flip a feature public when you're done testing and every user gets it instantly.</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="feature-gates-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="divide-y divide-slate-100">
        {entries.map((e) => {
          const isPublic = e.currently_public;
          return (
            <div key={e.key} className="px-5 py-3 flex items-center gap-3" data-testid={`feature-gate-row-${e.key}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{e.title}</div>
                {e.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{e.description}</div>}
                <div className="text-xs mt-1 flex items-center gap-1.5">
                  {isPublic ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-semibold">
                      <Globe className="w-3 h-3" /> Live for all users
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 font-semibold">
                      <Lock className="w-3 h-3" /> Your account only
                    </span>
                  )}
                  {e.released && (<><span className="text-slate-400">·</span><span className="text-slate-400">{formatDate(e.released)}</span></>)}
                </div>
              </div>
              <Button
                size="sm"
                variant={isPublic ? 'outline' : 'default'}
                onClick={() => toggle(e.key, !isPublic)}
                disabled={busy === e.key}
                className={isPublic ? 'text-amber-700 border-amber-200 hover:bg-amber-50' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                data-testid={`feature-gate-toggle-${e.key}`}
              >
                {busy === e.key
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : (isPublic ? 'Make it mine only' : 'Roll out to all users')}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
